import React from 'react';

type Props = { 
  children: React.ReactNode; 
  onReset?: () => void; 
  fallback?: React.ComponentType<{ error?: Error; resetError: () => void }>;
};

type State = { 
  hasError: boolean; 
  error?: Error; 
  errorInfo?: React.ErrorInfo;
};

export default class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('UI Crash:', error, errorInfo);
    
    // Armazena diagnóstico no window para debug
    (window as any).__FE_DIAG__ = (window as any).__FE_DIAG__ || [];
    (window as any).__FE_DIAG__.push({ 
      type: 'boundary', 
      error: error.message, 
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString()
    });

    this.setState({ error, errorInfo });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
    this.props.onReset?.();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        const FallbackComponent = this.props.fallback;
        return <FallbackComponent error={this.state.error} resetError={this.handleReset} />;
      }

      return (
        <div 
          role="alert" 
          className="min-h-screen flex items-center justify-center bg-background p-6"
          data-testid="error-boundary-fallback"
        >
          <div className="max-w-md w-full bg-card border border-destructive/20 rounded-lg p-6 shadow-lg dark:shadow-2xl dark:shadow-slate-900/50">
            <div className="text-center">
              <div className="mx-auto mb-4 w-12 h-12 bg-destructive/10 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-destructive" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              
              <h2 className="text-xl font-semibold text-foreground mb-2">
                Ops! Algo quebrou nesta tela
              </h2>
              
              <p className="text-sm text-muted-foreground mb-4">
                Não se preocupe, isso é temporário. Tente recarregar a página.
              </p>

              {process.env.NODE_ENV === 'development' && this.state.error && (
                <details className="mb-4 text-left">
                  <summary className="cursor-pointer text-sm font-medium text-muted-foreground mb-2">
                    Detalhes do erro (dev)
                  </summary>
                  <pre className="text-xs bg-muted p-3 rounded overflow-auto max-h-32">
                    {this.state.error.message}
                    {this.state.error.stack && '\n\n' + this.state.error.stack}
                  </pre>
                </details>
              )}

              <div className="flex gap-2 justify-center">
                <button 
                  onClick={this.handleReset}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                >
                  Tentar novamente
                </button>
                <button 
                  onClick={() => window.location.reload()}
                  className="px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 transition-colors"
                >
                  Recarregar página
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
